import { Caption1, Tooltip, makeStyles, mergeClasses, tokens } from "@fluentui/react-components";

/**
 * 글 옆의 회색 표식. 색은 막힘·지연·해결에만 쓴다.
 * 정도 막대에는 숫자를 다시 쓰지 않는다.
 */

export type InkVerdict = "thisWeek" | "nextWeek" | "blocked";
export type InkTrackState = "open" | "late" | "done";
export type InkShift = "held" | "sooner" | "later" | "done";

export const INK_VERDICT_LABEL: Record<InkVerdict, string> = {
  thisWeek: "이번 주",
  nextWeek: "다음 주",
  blocked: "막힘",
};

export const INK_TRACK_LABEL: Record<InkTrackState, string> = {
  open: "진행",
  late: "지연",
  done: "해결",
};

export const INK_SHIFT_LABEL: Record<InkShift, string> = {
  held: "유지",
  sooner: "앞당김",
  later: "미룸",
  done: "마침",
};

export interface InkWeek {
  key: string;
  /** 칸 아래 짧은 날짜. 화면에서 formatDate(..., "short")로 만든다. */
  label: string;
  current?: boolean;
  delayed?: boolean;
}

function clampExtent(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, Math.round(value)));
}

const useStyles = makeStyles({
  row: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
    minHeight: "32px",
  },
  sentence: {
    flex: "1 1 auto",
    minWidth: 0,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground1,
  },
  judgment: {
    flex: "0 1 auto",
  },
  mark: {
    flex: "0 0 auto",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
    whiteSpace: "nowrap",
  },
  blocked: { color: tokens.colorPaletteRedForeground1 },
  late: { color: tokens.colorPaletteDarkOrangeForeground1 },
  done: { color: tokens.colorPaletteGreenForeground1 },
  quiet: { color: tokens.colorNeutralForeground3 },
  extent: {
    position: "relative",
    flex: "0 0 56px",
    width: "56px",
    height: "4px",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralBackground5,
    overflow: "hidden",
  },
  extentGrow: {
    flex: "1 1 72px",
    width: "auto",
    maxWidth: "120px",
  },
  extentFill: {
    position: "absolute",
    top: 0,
    left: 0,
    height: "100%",
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorNeutralForeground4,
  },
  weeks: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    minWidth: 0,
  },
  phase: {
    flex: "0 0 auto",
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightRegular,
    whiteSpace: "nowrap",
  },
  cells: {
    display: "flex",
    alignItems: "flex-start",
    gap: "8px",
    minWidth: 0,
  },
  cell: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: "2px",
    minWidth: 0,
  },
  square: {
    width: "14px",
    height: "14px",
    boxSizing: "border-box",
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground5,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  squareCurrent: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStrokeAccessible}`,
  },
  squareDelayed: {
    backgroundColor: tokens.colorPaletteDarkOrangeBackground3,
    border: `1px solid ${tokens.colorPaletteDarkOrangeBackground3}`,
  },
  squareDelayedCurrent: {
    border: `1px solid ${tokens.colorNeutralStrokeAccessible}`,
  },
  cellLabel: {
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
    whiteSpace: "nowrap",
  },
  track: {
    display: "flex",
    flexDirection: "column",
    gap: "2px",
    minWidth: 0,
    paddingTop: "6px",
    paddingBottom: "6px",
  },
  trackTop: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
  },
  meta: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    minWidth: 0,
  },
  opened: {
    flex: "0 0 auto",
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
  },
  comment: {
    margin: 0,
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    color: tokens.colorNeutralForeground2,
    whiteSpace: "normal",
  },
  attach: {
    flex: "0 0 auto",
    color: tokens.colorNeutralForeground3,
    whiteSpace: "nowrap",
    background: "none",
    border: "none",
    padding: 0,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightRegular,
    lineHeight: tokens.lineHeightBase200,
    cursor: "default",
    ":focus-visible": {
      outline: `2px solid ${tokens.colorStrokeFocus2}`,
      outlineOffset: "2px",
    },
  },
  date: {
    flex: "0 0 auto",
    fontSize: tokens.fontSizeBase300,
    lineHeight: tokens.lineHeightBase300,
    fontWeight: tokens.fontWeightRegular,
    fontVariantNumeric: "tabular-nums",
    color: tokens.colorNeutralForeground2,
    whiteSpace: "nowrap",
  },
});

export function JudgmentRow({ text, verdict, extent }: { text: string; verdict: InkVerdict; extent: number }) {
  const styles = useStyles();
  const label = INK_VERDICT_LABEL[verdict];
  return (
    <div className={styles.row}>
      <Tooltip content={text} relationship="description">
        <span className={mergeClasses(styles.sentence, styles.judgment)}>{text}</span>
      </Tooltip>
      <Extent value={extent} />
      <span className={mergeClasses(styles.mark, verdict === "blocked" && styles.blocked)}>{label}</span>
    </div>
  );
}

export function WeekCells({ phase, weeks }: { phase: string; weeks: InkWeek[] }) {
  const styles = useStyles();
  return (
    <div className={styles.weeks} role="group" aria-label={`${phase}의 최근 주`}>
      <Caption1 className={styles.phase}>{phase}</Caption1>
      <div className={styles.cells}>
        {weeks.map((week) => (
          <span key={week.key} className={styles.cell}>
            <Tooltip content={weekDescribe(week)} relationship="description">
              <span
                className={mergeClasses(styles.square, week.current && styles.squareCurrent, week.delayed && styles.squareDelayed, week.current && week.delayed && styles.squareDelayedCurrent)}
                role="img"
                aria-label={weekDescribe(week)}
              />
            </Tooltip>
            <Caption1 className={styles.cellLabel}>{week.label}</Caption1>
          </span>
        ))}
      </div>
    </div>
  );
}

export function TrackRow({
  title,
  opened,
  state,
  extent,
  comment,
  attachments,
}: {
  title: string;
  opened: string;
  state: InkTrackState;
  extent: number;
  comment?: string;
  attachments?: string[];
}) {
  const styles = useStyles();
  const label = INK_TRACK_LABEL[state];
  const names = attachments?.filter(Boolean) ?? [];
  return (
    <div className={styles.track}>
      <div className={styles.trackTop}>
        <Tooltip content={title} relationship="description">
          <span className={styles.sentence}>{title}</span>
        </Tooltip>
        <span className={mergeClasses(styles.mark, state === "late" && styles.late, state === "done" && styles.done)}>{label}</span>
      </div>
      <div className={styles.meta}>
        <Caption1 className={styles.opened}>{opened}</Caption1>
        <Extent value={extent} grow />
        {names.length > 0 ? (
          <Tooltip content={names.join(", ")} relationship="description">
            <button type="button" className={styles.attach} aria-label={`첨부 ${names.length}건, ${names.join(", ")}`}>
              첨부 {names.length}
            </button>
          </Tooltip>
        ) : null}
      </div>
      {state === "done" && comment ? <p className={styles.comment}>{comment}</p> : null}
    </div>
  );
}

export function AgendaRow({ date, title, shift }: { date: string; title: string; shift: InkShift }) {
  const styles = useStyles();
  const label = INK_SHIFT_LABEL[shift];
  return (
    <div className={styles.row}>
      <span className={styles.date}>{date}</span>
      <Tooltip content={title} relationship="description">
        <span className={styles.sentence}>{title}</span>
      </Tooltip>
      <span className={mergeClasses(styles.mark, shift === "done" && styles.quiet)}>{label}</span>
    </div>
  );
}

function Extent({ value, grow }: { value: number; grow?: boolean }) {
  const styles = useStyles();
  const width = clampExtent(value);
  return (
    <span className={mergeClasses(styles.extent, grow && styles.extentGrow)} aria-hidden>
      <span className={styles.extentFill} style={{ width: `${width}%` }} />
    </span>
  );
}

function weekDescribe(week: InkWeek): string {
  if (week.current && week.delayed) return `${week.label}, 이번 주, 지연`;
  if (week.current) return `${week.label}, 이번 주`;
  if (week.delayed) return `${week.label}, 지연`;
  return week.label;
}
